import React from 'react';
import Header from '@amzn/awsui-components-console/header';
import Table from '@amzn/awsui-components-console/table';
import Box from '@amzn/awsui-components-console/box';
import Alert from '@amzn/awsui-components-console/alert';
import { ApolloError } from '@apollo/client';
import { Translation } from 'src/generated/graphql';
import { TranslationStatusIndicator } from 'src/common/translationStatus';
import {
  TablePreferences,
  useTablePreferences,
} from 'src/common/ui/TablePreferences';
import { LOCALES_TABLE_PREFERENCES } from './stringTablePreferences';

interface LocalesTableProps {
  translations: Translation[];
  loading: boolean;
  error?: ApolloError;
}

const LocalesTable: React.FC<LocalesTableProps> = ({
  translations,
  loading,
  error,
}) => {
  // Columns only: every locale renders, unpaged.
  const { preferences, onConfirm, columnDisplay } = useTablePreferences(
    LOCALES_TABLE_PREFERENCES,
  );

  if (error) {
    return (
      <Alert type="error" header="Failed to load translations">
        {error.message}
      </Alert>
    );
  }

  return (
    <Table
      columnDisplay={columnDisplay}
      wrapLines={preferences.wrapLines}
      stripedRows={preferences.stripedRows}
      contentDensity={preferences.contentDensity}
      preferences={
        <TablePreferences
          config={LOCALES_TABLE_PREFERENCES}
          preferences={preferences}
          onConfirm={onConfirm}
        />
      }
      header={
        <Header
          counter={
            !loading && translations.length > 0
              ? `(${translations.length})`
              : undefined
          }
        >
          Locales
        </Header>
      }
      loading={loading}
      loadingText="Loading translations…"
      items={translations}
      columnDefinitions={[
        {
          id: 'locale',
          header: 'Locale',
          cell: (item) => (
            <span data-testid="locale-row" data-locale={item.locale}>
              <span data-testid="locale-code">{item.locale}</span>
            </span>
          ),
        },
        {
          id: 'status',
          header: 'Status',
          cell: (item) => (
            <span data-testid="locale-status-badge">
              <TranslationStatusIndicator status={item.status} />
            </span>
          ),
        },
        {
          id: 'translatedText',
          header: 'Translation',
          cell: (item) => (
            <span data-testid="locale-translation">{item.translatedText}</span>
          ),
        },
      ]}
      empty={
        <Box textAlign="center" color="text-status-inactive" padding="l">
          No translations available for this string.
        </Box>
      }
    />
  );
};

export default LocalesTable;
